import React from 'react';
import { Box, CircularProgress, Fade, Popper } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import dompurify from 'dompurify';
import { classes } from '../../AIStudio/ApplicationSummaries/styles';
import { FelixConfused } from '../../AIStudio/SharedComponents/Graphics/FelixSVG';

interface CandidateGroupPopoverProps {
  candidateHovered: boolean;
  handleHoverCandidate: (target: HTMLElement | null, searchCandidateId?: number) => void;
  handleUnhoverCandidate: () => void;
  lastHoveredCandidateId: number | null;
  anchorEl: HTMLElement | null;
  searchId: number;
}

const CandidateGroupPopover: React.FC<CandidateGroupPopoverProps> = ({
  candidateHovered,
  handleHoverCandidate,
  handleUnhoverCandidate,
  lastHoveredCandidateId,
  anchorEl,
  searchId
}) => {
  const { data, isLoading } = useQuery({
    queryKey: ['search_candidate_summary', searchId, lastHoveredCandidateId],
    queryFn: async () => {
      if (!lastHoveredCandidateId) return null;
      const response = await fetch(
        `/api/v4/candidate_sourcing/searches/${searchId}/candidate_groups/${lastHoveredCandidateId}/ai_summary`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch summary');
      }
      return response.json();
    },
    enabled: candidateHovered && !!lastHoveredCandidateId
  });

  const summary = data?.summary;
  const isOpen = candidateHovered && anchorEl !== null;

  return (
    <Popper
      id={'search-candidate-summary-popper'}
      open={isOpen}
      anchorEl={anchorEl}
      placement="right"
      transition
      sx={classes.summaryPopper}
      onMouseEnter={() => handleHoverCandidate(anchorEl)}
      onMouseLeave={handleUnhoverCandidate}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={300}>
          <Box sx={classes.summaryPopover}>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <CircularProgress size={24} />
              </Box>
            ) : summary ? (
              <div dangerouslySetInnerHTML={{ __html: dompurify.sanitize(summary) }} />
            ) : (
              <>
                <Box sx={classes.noSummaryFelix}>
                  <FelixConfused />
                </Box>
                <Box sx={classes.noSummary}>No summary available</Box>
              </>
            )}
          </Box>
        </Fade>
      )}
    </Popper>
  );
};

export default CandidateGroupPopover;
